/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable consistent-return */
import { DialogClose } from '@/components/_ui/Dialog'
import useGlobalStore from '@/store/useGlobalStore'
import { useEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ProductVariantFlag } from '@/components/_products/ProductVariantFlag'
import T from '@/components/Helpers/Translate'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import { useExclusiveProduct } from '@/lib/hooks/helpers/useExclusiveProduct'
import { useFocusMode } from '@/lib/hooks/helpers/useFocusMode'
import { useProductVariationTypeQueryParams } from '@/lib/hooks/helpers/useProductVariationTypeQueryParams'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import {
  getSkuAvailability,
  useGetSkuAvailability,
} from '@/lib/hooks/services/useGetSkuAvailability'
import { getTerms } from '@/lib/hooks/services/useGetTerms'
import type { MixpanelProductData } from '@/lib/services/mixpanel/helpers/generateCartEventData'
import {
  generateAddToCartEventData,
  generateProductEventData,
} from '@/lib/services/mixpanel/helpers/generateCartEventData'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { cn } from '@/lib/utils'
import { getDefaultSelectedVariation } from '@/lib/utils/product/getDefaultSelectedVariation'
import { getProductRecommendationSlugArray } from '@/lib/utils/shared/getProductRecommendationSlugArray'
import type { CartEventLocation, CartItemType } from '@/store/types/cart'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMediaQuery } from 'react-responsive'
import { toast } from 'sonner'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import { useQuickSub } from '@/lib/hooks/helpers/useQuickSub'
import { Button } from '../_ui/Button'
import { ErrorAlert } from '../_ui/ErrorAlert'
import AddToCartButton from '../AddToCartButton/AddToCartButton'
import { useModals } from '../ModalManager/useModals'
import { AddProductToSubscription } from './_components/AddProductToSubscription'
import { ExclusiveProductLock } from './_components/ExclusiveProductLock'
import FrequentlyBoughtTogether from './_components/FrequentlyBoughtTogether'
import ProductCarousel from './_components/ProductCarousel'
import ProductDelivery from './_components/ProductDelivery'
import { ProductFlavorSelector } from './_components/ProductFlavorSelector'
import { ProductPurchaseOptions } from './_components/ProductPurchaseOptions'
import { getDefaultSelectedPurchaseOption } from './_components/ProductPurchaseOptions/_helpers/getDefaultSelectedPurchaseOption'
import ProductShareButton from './_components/ProductShareButton'
import { QuantityControls } from './_components/QuantityControls'
import { ProductPageSections } from './_sections'
import { ProductDetailsSubtitle } from './_components/ProductDetailsSubtitle'
import { HsaFsaEligible } from './_components/HsaFsaEligible'

type Props = {
  productData: CosmicProductType
  isQuickAdd?: boolean
}

export const ProductDetails = ({ productData, isQuickAdd }: Props) => {
  const {
    app: { addProductToSubscription },
    setApp,
    setAppAddProductToSubscription,
    addToCart,
    setUser,
    setLoginPopoverOpen,
    user: { customerType, isSignedIn },
    app: { marketExceptions },
    setCheckout,
  } = useGlobalStore()

  // Uses useDoesUserHaveActiveSubscription() hook inside
  const { willShowUnlockedProductFlow } = useExclusiveProduct({
    categories: productData.categories,
  })

  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()

  const { isLoading: isLoadingProductAvailability, productAvailability } =
    useGetSkuAvailability({
      product: productData,
    })

  const { modals } = useModals()
  const { query } = useRouter()
  const { gtm } = useGTM()
  const { isFocusMode, isLoadingIsFocusMode } = useFocusMode()
  const { clientRedirect } = useClientRedirect()

  const isDesktop = useMediaQuery({ minWidth: 1024 })

  const [itemCount, setItemCount] = useState(1)
  const { showQuickSub } = useQuickSub()

  const [selectedVariation, setSelectedVariation] =
    useState<CosmicVariationType | null>(
      productData.variations
        ? getDefaultSelectedVariation({
            variations: productData.variations,
            query,
          })
        : null,
    )

  // Add effect to update selectedVariation when query changes
  useEffect(() => {
    if (productData.variations) {
      const newVariation = getDefaultSelectedVariation({
        variations: productData.variations,
        query,
      })
      setSelectedVariation(newVariation)
    }
  }, [query.sku, productData.variations])

  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState<
    string | null
  >(
    getDefaultSelectedPurchaseOption({
      purchaseOptions: productData.purchaseOptions,
    }),
  )

  const isPurchasingDisabled = useMemo(() => {
    // Show subscription quick add modal if query param is true

    if (marketExceptions?.hasProductAvailability) {
      const skuAvailability = getSkuAvailability({
        sku: selectedVariation?.sku,
        productAvailability,
      })

      if (skuAvailability === 'out_of_stock') {
        return true
      }
    }

    if (
      !productData.purchaseOptions ||
      productData.purchaseOptions.length === 0
    ) {
      return true
    }

    return (
      !selectedVariation ||
      !selectedVariation.pricing ||
      selectedVariation.isDisable ||
      (isLoadingProductAvailability && isQuickAdd)
    )
  }, [
    selectedVariation,
    isLoadingProductAvailability,
    isQuickAdd,
    productAvailability,
    marketExceptions,
    productData.purchaseOptions,
  ])

  useEffect(() => {
    if (!query.referralCode) return
    // Once the user is signed in, we don't need to add referrals
    // This avoids changing their customer type back to customer,
    // which would show incorrect pricing if they're logged in
    if (isSignedIn) return

    setUser({
      refID: query.referralCode as string,
      refName: null,
      customerType: 'Customer',
    })
  }, [query.referralCode, setUser, isSignedIn])

  useEffect(() => {
    // Show subscription quick add modal if query param is true
    if (showQuickSub && isSignedIn) {
      setAppAddProductToSubscription({
        isSelectActiveSubscriptionModalOpen: true,
      })
      // If not signed in, show login popover
    } else if (showQuickSub && !isSignedIn) {
      setLoginPopoverOpen(true)
    }
  }, [
    isSignedIn,
    setAppAddProductToSubscription,
    setLoginPopoverOpen,
    showQuickSub,
  ])
  useEffect(() => {
    if (!query.referralCode) return
    // Once the user is signed in, we don't need to add referrals
    // This avoids changing their customer type back to customer,
    // which would show incorrect pricing if they're logged in
    if (isSignedIn) return
    mixpanelService.registerSuperProperties({
      referral_code: query.referralCode as string,
    })
    setUser({
      refID: query.referralCode as string,
      refName: null,
      customerType: 'Customer',
    })
  }, [query.referralCode, setUser, isSignedIn])

  useEffect(() => {
    // Add a small delay to ensure super properties are registered
    const timeoutId = setTimeout(() => {
      const dataToSend: {
        cart: MixpanelProductData[]
        focusMode: boolean
        is_unicity_lab_product_locked?: boolean
        scenario: string
      } = {
        cart: generateProductEventData(
          productData,
          selectedVariation ||
            getDefaultSelectedVariation({
              variations: productData.variations,
              query,
            }),
          marketExceptions?.currencyCode || 'USD',
          getTerms({
            forCustomerType: customerType,
            pricingData: selectedVariation?.pricing || null,
          })?.priceEach || 0,
          {
            purchase_type:
              getDefaultSelectedPurchaseOption({
                purchaseOptions: productData.purchaseOptions,
              }) || '',
          },
        ),
        focusMode: isFocusMode,
        scenario: isQuickAdd ? 'quick_add_page' : 'product_page',
      }
      if (
        selectedVariation?.flag?.label &&
        selectedVariation.flag.label === 'ULabs'
      ) {
        dataToSend.is_unicity_lab_product_locked = !willShowUnlockedProductFlow
      }
      mixpanelService
        .trackEvent(MixpanelEvent.VIEW_PRODUCT, dataToSend)
        .catch(() => {})
    }, 50)

    return () => clearTimeout(timeoutId)
  }, [selectedVariation, isFocusMode, willShowUnlockedProductFlow])

  const isAccessory = useMemo(
    () =>
      !!productData.categories?.find(
        (categoryData) => categoryData.slug === 'accessories',
      ),
    [productData.categories],
  )

  useProductVariationTypeQueryParams({ selectedVariation, isQuickAdd })

  const openGuaranteeDialog = () => {
    if (!productData.policies) return

    modals.openModal({
      type: 'modal',
      modalId: 'our_money_back_guarantee_checkout',
      modal: {
        titleIcon: productData.policies[0].icon,
        className: 'max-w-3xl',

        children: (
          <div>
            <div
              dangerouslySetInnerHTML={{
                __html: productData.policies[0].content,
              }}
            />

            <DialogClose>
              <Button className="mt-4 p-0 underline" variant="link">
                <T>Close</T>
              </Button>
            </DialogClose>
          </div>
        ),
      },
    })
  }

  const renderProductDescription = () => {
    if (isQuickAdd) {
      return (
        <div className="mb-2 text-sm">
          <Link
            onClick={() => {
              modals.closeModals()
            }}
            href={parseLinkUrlWithLocale({
              url: `/product/${productData.slug}`,
            })}
          >
            <T>view_full_details</T>
          </Link>
        </div>
      )
    }

    if (!selectedVariation?.description) {
      return (
        <ErrorAlert
          error={{
            error: {
              message: 'Missing product description',
            },
          }}
        />
      )
    }

    return (
      <div
        dangerouslySetInnerHTML={{
          __html: selectedVariation.description,
        }}
        className="mt-4 block leading-[30px]"
        data-test="product-description"
      />
    )
  }

  const renderFlavorSelector = () => {
    if (isQuickAdd) {
      if (!selectedVariation) return null

      return (
        <ProductFlavorSelector
          isQuickAdd
          attributes={productData.attributes}
          variations={productData.variations}
          selectedVariation={selectedVariation}
          setSelectedVariation={setSelectedVariation}
        />
      )
    }

    if (productData.variations && selectedVariation) {
      return (
        <ProductFlavorSelector
          isQuickAdd={false}
          attributes={productData.attributes}
          variations={productData.variations}
          selectedVariation={selectedVariation}
          setSelectedVariation={setSelectedVariation}
        />
      )
    }
  }

  const renderPurchaseOptions = () => {
    if (!willShowUnlockedProductFlow) return null

    if (isQuickAdd) {
      if (!selectedVariation) return null

      return (
        <ProductPurchaseOptions
          isQuickAdd={isQuickAdd}
          selectedVariation={selectedVariation}
          selectedPurchaseOption={selectedPurchaseOption}
          setSelectedPurchaseOption={setSelectedPurchaseOption}
          purchaseOptions={productData.purchaseOptions}
          subscriptionDiscount={productData.subscriptionDiscount}
        />
      )
    }

    if (productData.variations && selectedVariation) {
      return (
        <ProductPurchaseOptions
          isQuickAdd={isQuickAdd}
          selectedVariation={selectedVariation}
          selectedPurchaseOption={selectedPurchaseOption}
          setSelectedPurchaseOption={setSelectedPurchaseOption}
          purchaseOptions={productData.purchaseOptions}
          subscriptionDiscount={productData.subscriptionDiscount}
        />
      )
    }
  }

  if (!selectedVariation) {
    return (
      <ErrorAlert
        error={{
          error: {
            message: 'Unable to load product variation.',
            code: 400,
          },
        }}
      />
    )
  }

  const itemToAddToCart: CartItemType = {
    subscriptionDiscount: productData.subscriptionDiscount || 0,
    slug: productData.slug,
    title: productData.name,
    image:
      selectedVariation.media?.imageUrls?.[0]?.url ||
      selectedVariation.media?.[0]?.url ||
      productData.imageUrl ||
      '',
    description: productData.description,
    po: selectedPurchaseOption === 'onetime' ? 'otp' : 'subscribe',
    qty: itemCount,
    product: selectedVariation,
    purchaseOptions: productData.purchaseOptions || null,
    guarantee: productData.policies?.[0] || null,
    recommendedProducts: getProductRecommendationSlugArray({
      productData,
    }),
    addToCartLocation: ['product_page_main_button'],
    categories: productData.categories,
  }

  const addToCartSectionRenderer = () => {
    if (!willShowUnlockedProductFlow) return null

    const buyNowButton = () => (
      <Button
        disabled={isPurchasingDisabled}
        className="h-[50px] w-full p-6 text-lg"
        onClick={async () => {
          setCheckout({
            scenario: isQuickAdd ? 'quick_add_page' : 'product_page',
          })

          addToCart({
            ...itemToAddToCart,
            ...(isQuickAdd &&
              productData.variations &&
              !('error' in productData) && {
                purchaseOptions: productData.purchaseOptions,
              }),
          })

          modals.closeModals()

          await clientRedirect.to({
            path: `/checkout`,
          })
        }}
      >
        <T>buy</T>
      </Button>
    )

    const addToCartSection = () => (
      <div className="flex w-full flex-row justify-between gap-3 md:gap-16">
        <AddToCartButton
          disabled={isPurchasingDisabled}
          className="mt-0 h-[50px]"
          data-test="product-add-to-cart"
          onClick={() => {
            if (
              itemToAddToCart.po === 'subscribe' &&
              addProductToSubscription.userHasActiveSubscription
            ) {
              setAppAddProductToSubscription({
                isSelectActiveSubscriptionModalOpen: true,
              })

              return
            }

            gtm.addToCart({
              cartItem: itemToAddToCart,
              event_location: 'product_page_main_button',
            })

            const addToCartLocation: CartEventLocation[] = []
            for (let index = 1; index <= itemCount; index += 1) {
              addToCartLocation.push('product_page_main_button')
            }
            mixpanelService
              .trackEvent(MixpanelEvent.ADD_TO_CART, {
                event_location: isQuickAdd
                  ? 'quick_add_page_main_button'
                  : 'product_page_main_button',
                cart: generateAddToCartEventData({
                  cartItem: { ...itemToAddToCart, addToCartLocation },
                  currency: marketExceptions?.currencyCode || 'USD',
                  price:
                    getTerms({
                      forCustomerType: customerType,
                      pricingData: selectedVariation?.pricing || null,
                    })?.priceEach || 0,
                  quantity: itemCount,
                  addToCartLocation: isQuickAdd
                    ? 'quick_add_page_main_button'
                    : 'product_page_main_button',
                  focus: isFocusMode,
                }),
                item_count: itemCount,
              })
              .catch(() => {})

            if (isQuickAdd) {
              toast.success('Added to cart')
              modals.closeModals()
            } else {
              setApp({ isCartOpen: true })
            }

            addToCart({
              ...itemToAddToCart,
              addToCartLocation,
              ...(isQuickAdd &&
                productData.variations &&
                !('error' in productData) && {
                  purchaseOptions: productData.purchaseOptions,
                }),
            })
          }}
        >
          <T>add_to_cart</T>
        </AddToCartButton>
      </div>
    )

    if (isDesktop || !isQuickAdd) {
      return (
        <div className="mt-3 flex w-full flex-col gap-3">
          {addToCartSection()}
          {buyNowButton()}
        </div>
      )
    }

    return (
      <div
        style={{ zIndex: 1000 }}
        className="fixed inset-x-0 bottom-0 flex w-full flex-col gap-3 border-t bg-white p-3"
      >
        {addToCartSection()}
        {buyNowButton()}
      </div>
    )
  }

  const productDeliveryAndCartControlsRenderer = () => {
    if (!willShowUnlockedProductFlow) return null

    return (
      <div className="mt-3 flex flex-row items-center justify-between gap-2 rounded-lg p-3">
        <ProductDelivery
          isLoadingProductAvailability={isLoadingProductAvailability}
          productAvailability={productAvailability}
          selectedVariation={selectedVariation}
        />
        <QuantityControls itemCount={itemCount} setItemCount={setItemCount} />
      </div>
    )
  }

  return (
    <div>
      <AddProductToSubscription
        isLoadingProductAvailability={isLoadingProductAvailability}
        productAvailability={productAvailability}
        itemToAddToSubscription={itemToAddToCart}
      />
      <div
        className={cn(
          'm-auto mt-4 flex h-auto w-full flex-col justify-center gap-7 p-5 text-primary md:max-w-352 md:flex-row',
          isQuickAdd && 'p-0',
        )}
      >
        {selectedVariation.media && (
          <ProductCarousel
            images={
              selectedVariation.media.imageUrls ?? selectedVariation.media
            }
            flagData={selectedVariation.flag}
            className="hidden md:block"
            selectedVariation={selectedVariation}
            productData={productData}
          />
        )}

        <div className="mb-5 flex w-full flex-col overflow-x-hidden md:w-1/2">
          <div className="relative flex w-full flex-col justify-between md:flex-row">
            <h1
              className="mb-2 text-2xl font-semibold lg:text-4xl"
              data-test="product-title"
            >
              {productData.name}
            </h1>

            <span className="inline md:hidden">
              <ProductDetailsSubtitle selectedVariation={selectedVariation} />
              {isSignedIn && !isQuickAdd && (
                <ProductShareButton
                  product={productData}
                  selectedVariation={selectedVariation}
                />
              )}
            </span>

            <div className="relative mt-2">
              {selectedVariation.flag?.label && !isQuickAdd && (
                <ProductVariantFlag
                  text={selectedVariation.flag.label ?? ''}
                  textColor={selectedVariation.flag.style?.textColor ?? ''}
                  backgroundColor={
                    selectedVariation.flag.style?.backgroundColor ?? ''
                  }
                  className="absolute -top-2 right-0 md:hidden"
                />
              )}

              {selectedVariation.media && !isQuickAdd && (
                <ProductCarousel
                  images={
                    selectedVariation.media.imageUrls ?? selectedVariation.media
                  }
                  className="md:hidden"
                  selectedVariation={selectedVariation}
                  productData={productData}
                />
              )}
            </div>
          </div>

          <span className="hidden md:inline">
            <ProductDetailsSubtitle selectedVariation={selectedVariation} />
            {isSignedIn && !isQuickAdd && (
              <ProductShareButton
                product={productData}
                selectedVariation={selectedVariation}
                className=""
              />
            )}
          </span>
          {renderProductDescription()}

          <ErrorBoundary
            fallback={
              <ErrorAlert
                error={{
                  error: {
                    code: 500,
                    message: 'Error loading flavor selector',
                  },
                }}
              />
            }
          >
            <div
              className={cn('py-6', isQuickAdd && 'py-2')}
              data-test="product-variants"
            >
              {renderFlavorSelector()}
            </div>
          </ErrorBoundary>

          {renderPurchaseOptions()}
          {productDeliveryAndCartControlsRenderer()}
          {addToCartSectionRenderer()}
          {!isQuickAdd && willShowUnlockedProductFlow && (
            <div className="mt-4 flex flex-col items-center justify-center space-y-3 lg:flex-row lg:justify-between lg:space-y-0 lg:space-x-3">
              <div>
                <HsaFsaEligible
                  selectedPurchaseOption={selectedPurchaseOption}
                />
              </div>
              {productData.policies && productData.policies[0] && (
                <Button
                  className="h-full p-0 font-normal whitespace-normal text-[#0B3B6199] underline"
                  onClick={() => openGuaranteeDialog()}
                  variant="link"
                  data-test="product-guarantee"
                >
                  {productData.policies[0].buttonText}
                </Button>
              )}
            </div>
          )}

          <div className="max-w-[90vw]">
            {!isFocusMode &&
              !isLoadingIsFocusMode &&
              !isQuickAdd &&
              willShowUnlockedProductFlow && (
                <FrequentlyBoughtTogether productData={productData} />
              )}
            {!willShowUnlockedProductFlow && (
              <ExclusiveProductLock
                exclusiveLockedContentData={
                  productData.exclusiveLockedContentData
                }
                itemLocked={itemToAddToCart}
              />
            )}
          </div>
        </div>
      </div>
      {!isQuickAdd && (
        <ProductPageSections
          product={productData}
          selectedVariation={selectedVariation}
          belowFoldSections={productData.contents}
          isAccessory={isAccessory}
        />
      )}
    </div>
  )
}
